 import PropTypes from 'prop-types';
import { createRef, Suspense } from 'react';
import { createPortal } from 'react-dom';

import Button from 'Component/Button';
import ClickOutside from 'Component/ClickOutside';
import CrossIcon from 'Component/CrossIcon';
import { BLACK_KEY, GREEN_KEY, M_KEY } from 'Component/Icon/Icon.config';
import Overlay from 'Component/Overlay/Overlay.component';
import { lazyComponentLoader } from 'Util/lazyComponentLoader';

const NotificationList = lazyComponentLoader(() => import('Component/NotificationList'));

import { SIZE_NONE, TYPE_TEXT, WIDTH_HUG } from '../Button/Button.config';
import { ESCAPE_KEY } from './Popup.config';

/** @namespace Bodypwa/Component/Popup/Component */
export class PopupComponent extends Overlay {
    static propTypes = {
        ...Overlay.propTypes,
        clickOutside: PropTypes.bool,
        title: PropTypes.string,
        className: PropTypes.string,
        iconPosition: PropTypes.string,
        headerPosition: PropTypes.string,
        clssWidthPopup: PropTypes.string,
        hideButton: PropTypes.string,
        noPadding: PropTypes.string,
        icon: PropTypes.elementType
    };

    static defaultProps = {
        ...Overlay.defaultProps,
        clickOutside: true,
        title: '',
        className: '',
        iconPosition: '',
        headerPosition: '',
        clssWidthPopup: 'max-w-490 w-full',
        hideButton: 'hidden',
        noPadding: 'px-0',
        icon: null,
        closeOnBrowserBack: true
    };

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        super.componentDidMount();
    }

    componentDidUpdate(prevProps) {
        const { shouldPopupClose, resetHideActivePopup } = this.props;
        const { shouldPopupClose: prevShouldPopupClose } = prevProps;

        if (shouldPopupClose && shouldPopupClose !== prevShouldPopupClose) {
            this.hidePopUp();
            resetHideActivePopup();
        }

        super.componentDidUpdate(prevProps);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    renderTitle() {
        const { title, icon: TitleIcon } = this.props;

        if (!title) {
            return null;
        }

        return (
            <h3
                className="no-underline text-p16 my-0 flex gap-2 text-bmText-default"
            >
                { TitleIcon ? <TitleIcon color={ GREEN_KEY } size={ M_KEY } /> : null }
                { ' ' }
                { title }

            </h3>
        );
    }

    // Same with click outside
    handleClickOutside = () => {
        const { clickOutside } = this.props;

        if (!clickOutside) {
            return;
        }
        this.hidePopupAndGoBack();
    };

    handleKeyDown = (e) => {
        switch (e.keyCode) {
        case ESCAPE_KEY:
            this.hidePopupAndGoBack();
            break;
        default:
            break;
        }
    };

    // fromBrowserBack is forwarded to onClose so consumers can treat a Back press as cancel.
    onOverlayClose(fromBrowserBack = false) {
        const {
            hideActiveOverlay, goToPreviousNavigationState, onClose, setIsClosing
        } = this.props;
        const isVisible = this.getIsVisible();

        if (isVisible) {
            setIsClosing(true);
            this.unfreezeScroll();
            hideActiveOverlay();
            goToPreviousNavigationState();
            onClose(fromBrowserBack);
            setIsClosing(false);
        }
    }

    hidePopUp = () => {
        this.requestOverlayClose();
    };

    renderCloseButton() {
        const { iconPosition, hideButton } = this.props;
        const classHideButton = hideButton ? 'hidden' : '';
        return (
            <div
                className={ `${iconPosition}` }
            >
                <Button
                    onClick={ this.hidePopupAndGoBack }
                    type={ TYPE_TEXT }
                    width={ WIDTH_HUG }
                    size={ SIZE_NONE }
                >
                    <CrossIcon
                        size={ M_KEY }
                        color={ BLACK_KEY }
                        className={ `${classHideButton} duration-200 ease-in-out 
                        group-hover:fill-neutral-500 group-active:fill-neutral-300` }
                    />
                </Button>
            </div>
        );
    }

    renderNotifications() {
        const { isMobile } = this.props;

        if (!isMobile) {
            return null;
        }

        return (
            <Suspense fallback={ null }>
                <NotificationList
                    className="w-screen top-0 relative!"
                />
            </Suspense>
        );
    }

    renderContent() {
        const {
            children, 
            contentMix,
            popUpHeight, 
            isHideHeader, 
            headerRef, 
            className,
            headerPosition, 
            clssWidthPopup, 
            hasConatiner
        } = this.props;
        const isVisible = this.getIsVisible();
        // TODO remove classNoPadding when redisian de Popup
        const style = popUpHeight > 0 ? { height: popUpHeight } : {};
        if (!isVisible) {
            return null;
        }

        const spacing = children ? 'px-4 py-6 lg:p-6 bg-bmBg-default' : '';

        const content = hasConatiner() ? (
            <div className={ `${ spacing }` }>
                { children }
            </div>
        ) : children;

        return (
            <ClickOutside onClick={ this.handleClickOutside }>
                <div
                    className={ `bg-bmBg-default max-h-[80vh] overflow-x-hidden
                         overflow-y-auto relative rounded-none bottom-0 overflow-auto webkit-touch-scroll
                    ${className}
                    ${clssWidthPopup}` }
                    style={ style }
                    mix={ contentMix }
                >
                    <header
                       
                        className={ `flex justify-between items-center
                             px-4 py-4 lg:p-6 sticky top-0 bg-bmBg-default 
                            border-b-neutral-200 border-b border-solid z-40 leading-0
                         ${headerPosition}
                          ${isHideHeader ? 'invisible hidden' : ''}` }
                        ref={ headerRef || createRef() }
                    >
                        { this.renderTitle() }
                        { this.renderCloseButton() }
                    </header>
                    { content }
                </div>
            </ClickOutside>
        );
    }

    hidePopupAndGoBack = (e) => {
        if (e?.stopPropagation) {
            e.stopPropagation();
        }

        const { onCustomClose } = this.props;
        this.hidePopUp();
        if (onCustomClose && typeof onCustomClose === 'function') {
            onCustomClose();
        }
    };

    renderOverlay() {
        const { overlayHtml } = this.props;
        const {
            isClosing
        } = this.props;
        const isVisible = this.getIsVisible();

        if (overlayHtml) {
            return overlayHtml;
        }

        return (
            <div
                className={ `fixed top-0 left-0 w-screen h-screen z-900 bg-black-transparent
              transition-opacity duration-500 ease-in-out opacity-0 
              ${isVisible ? 'opacity-[1]' : ''} ${isClosing ? 'opacity-0' : ''}` }
            />
        );
    }

    render() {
        const {
            mix = {}, areOtherOverlaysOpen, isClosing, isNoTransition, zIndex
        } = this.props;

        const isVisible = this.getIsVisible();
        const instant = { isInstant: areOtherOverlaysOpen };
        return createPortal(
            <div
                ref={ this.overlayRef }
                className={ `opacity-0  z-1000 inset-s-0 w-screen h-full items-end top-0 ${zIndex}
                ${isVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none'}
                ${instant}` }
                mix={ { ...mix, mods: { ...(mix?.mods || {}), isVisible, isClosing } } }
            >
                { this.renderOverlay() }
                <div
                    className={ `bottom-[-100vh] z-1000  transition-bottom duration-500 ease-in-out w-screen fixed
                    lg:translate-y-0  flex justify-center lg:items-center
                    items-end transition-transform duration-500 ease-in-out 
                    overscroll-contain pb-[env(safe-area-inset-bottom)]
                    ${isNoTransition ? 'transition-none!' : ''}
                    ${isVisible ? 'bottom-0! h-screen w-screen lg:top-0 translate-y-0 opacity-100' :
                         'translate-y-full opacity-0'}
                    ${isClosing ? 'bottom-[-100vh]!' : ''}
                    ` }
                >
                    { this.renderContent() }
                </div>
            </div>,
            document.body
        );
    }
}

export default PopupComponent;
